// src/components/Breadcrumb.jsx
import PropTypes from "prop-types";
import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { FaAngleLeft } from "react-icons/fa";

const normalizeHref = (p = "/home") => (p.startsWith("/") ? p : `/${p}`);

const Breadcrumb = ({ pageTitle = "", prevUrl }) => {
  const navigate = useNavigate();
  const href = prevUrl ? normalizeHref(prevUrl) : null;

  return (
    <div className="breadcrumb-appbar" role="navigation" aria-label="Breadcrumb">
      <style>{`
        :root{
          --brand:#107b49;
          --brand-2:#0fb66e;
          --ink:#0f172a;
          --muted:#6b7a91;
          --border: rgba(16,123,73,.12);
        }
        @media (prefers-color-scheme: dark){
          :root{ --ink:#e5e7eb; --muted:#9fb2d2; --border: rgba(255,255,255,.10); }
        }

        .breadcrumb-appbar{
          position: sticky; top: 0; z-index: 980;
          background: linear-gradient(180deg, rgba(247,251,249,.92), rgba(255,255,255,.9));
          backdrop-filter: blur(10px);
          -webkit-backdrop-filter: blur(10px);
          border-bottom: 1px solid var(--border);
          padding-top: env(safe-area-inset-top);
        }
        @media (prefers-color-scheme: dark){
          .breadcrumb-appbar{
            background: linear-gradient(180deg, rgba(17,24,39,.70), rgba(17,24,39,.62));
          }
        }

        .crumb-inner{
          max-width: 1200px; margin: 0 auto;
          padding: 25px 12px;
          display: grid;
          grid-template-columns: 48px 1fr 48px; /* left back • title • right spacer */
          align-items: center; gap: 8px;
        }

        .back-link{
          display: inline-flex; align-items: center; gap: 8px;
          padding: 8px 10px; border-radius: 12px;
          border: 1px solid rgba(0,0,0,.06);
          background: #fff; color: #065f46; font-weight: 600;
          text-decoration: none; cursor: pointer;
          transition: transform .1s ease, box-shadow .2s ease, background .15s ease;
        }
        .back-link:hover{ background:#f3faf6; box-shadow:0 6px 14px rgba(16,123,73,.12); }
        .back-link:active{ transform: scale(.98); }
        @media (prefers-color-scheme: dark){
          .back-link{ background:#0f172a; border-color: rgba(255,255,255,.12); color:#a7f3d0; }
          .back-link:hover{ background:#0c1321; }
        }

        .page-title{
          margin: 35px; text-align: center; color: var(--ink);
          font-size: 16.5px; line-height: 1.2; font-weight: 700;
          white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
        }

        /* slightly smaller on very small phones */
        @media (max-width: 380px){
          .page-title{ font-size: 15.5px; }
        }
      `}</style>

      <div className="crumb-inner">
        <div>
          {href ? (
            <Link to={href} className="back-link" aria-label="Go back">
              <FaAngleLeft aria-hidden="true" /> Back
            </Link>
          ) : (
            <button
              type="button"
              className="back-link"
              onClick={() => navigate(-1)}
              aria-label="Go back"
            >
              <FaAngleLeft aria-hidden="true" /> Back
            </button>
          )}
        </div>

        <h4
          className="page-title"
          aria-current="page"
          title={pageTitle}  // show full text on hover
        >
          {pageTitle}
        </h4>

        {/* right spacer slot (keeps title centered) */}
        <div aria-hidden="true" />
      </div>
    </div>
  );
};

Breadcrumb.propTypes = {
  pageTitle: PropTypes.string,
  prevUrl: PropTypes.string, // e.g., "/home" or "home"
};

export default Breadcrumb;
